import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { isDevEnvironment } from '../../utils/environment';

// Cache global de templates para evitar re-fetch
const templateCache = new Map();

/**
 * HtmlTemplatePreview - Renderiza preview de template HTML do Static
 * 
 * ESTRATÉGIA:
 * 1. Carrega template HTML uma única vez (cached)
 * 2. Re-renderiza apenas quando dados mudam (comparação deep)
 * 3. Usa requestAnimationFrame para atualização suave
 * 4. Aplica estilos via CSS variables no :root
 */
export const HtmlTemplatePreview = ({ templateUrl, formData = {} }) => {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templateHtml, setTemplateHtml] = useState(null);
  const [loadedTemplateUrl, setLoadedTemplateUrl] = useState(null);

  const isDebugEnabled = (() => {
    try {
      return typeof window !== 'undefined' && window.localStorage?.getItem('debugTemplatePreview') === '1';
    } catch {
      return false;
    }
  })();

  const debugLog = (...args) => {
    if (isDebugEnabled) console.debug(...args);
  };
  
  // Refs para controle de renderização
  const lastDataHashRef = useRef(null);
  const pendingRenderRef = useRef(null);
  const isMountedRef = useRef(true);

  // Carregar template quando URL muda, com proteção contra race condition
  useEffect(() => {
    debugLog('[HtmlTemplatePreview] templateUrl changed', { templateUrl });
    isMountedRef.current = true;
    lastDataHashRef.current = null; // Reset hash para forçar re-render com novo template
    let aborted = false;
    const currentUrl = templateUrl;
    
    const loadTemplate = async () => {
      if (!templateUrl) {
        setError('Template URL não fornecida');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Checar cache primeiro
        if (templateCache.has(templateUrl)) {
          debugLog('[HtmlTemplatePreview] template cache hit', { templateUrl });
          if (isMountedRef.current && !aborted && currentUrl === templateUrl) {
            setTemplateHtml(templateCache.get(templateUrl));
            setLoadedTemplateUrl(resolveTemplateUrl(templateUrl));
            setLoading(false);
          }
          return;
        }

        // Fetch template
        const fullUrl = resolveTemplateUrl(templateUrl);
        debugLog('[HtmlTemplatePreview] fetching template', { fullUrl });
        
        const response = await fetch(fullUrl);
        if (!response.ok) {
          throw new Error(`Falha ao buscar template: ${response.status}`);
        }
        
        const html = await response.text();
        debugLog('[HtmlTemplatePreview] template loaded', { bytes: html.length });
        
        // Cache template
        templateCache.set(templateUrl, html);
        
        if (isMountedRef.current && !aborted && currentUrl === templateUrl) {
          setTemplateHtml(html);
          setLoadedTemplateUrl(fullUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('[HtmlTemplatePreview] Erro ao carregar template:', err);
        if (isMountedRef.current) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    loadTemplate();

    return () => {
      isMountedRef.current = false;
      aborted = true;
    };
  }, [templateUrl]);

  // Função para criar hash estável dos dados
  const createDataHash = useCallback((data) => {
    const relevantData = {
      templateId: data?.templateId || '',
      title: data?.title || '',
      subtitle: data?.subtitle || '',
      logoUrl: data?.logoUrl || '',
      avatarShape: data?.avatarShape || 'round',
      socialLinks: JSON.stringify(data?.socialLinks || {}),
      genericLinks: JSON.stringify((data?.genericLinks || []).filter(l => l?.url)),
      customStyles: JSON.stringify(data?.customStyles || {}),
      // Professional template fields
      displayName: data?.displayName || '',
      specialty: data?.specialty || '',
      registrationNumber: data?.registrationNumber || '',
      photoUrl: data?.photoUrl || '',
      aboutHtml: data?.aboutHtml || '',
      contact: JSON.stringify(data?.contact || {}),
      experience: JSON.stringify(data?.experience || []),
      services: JSON.stringify(data?.services || []),
      testimonials: JSON.stringify(data?.testimonials || []),
      booking: JSON.stringify(data?.booking || {}),
    };
    return JSON.stringify(relevantData);
  }, []);

  // Renderizar preview quando template ou dados mudam
  useEffect(() => {
    if (!templateHtml || !iframeRef.current) return;

    // ⚠️ VALIDAÇÃO CRÍTICA: Garantir que templateHtml corresponde ao templateUrl atual
    const expectedUrl = resolveTemplateUrl(templateUrl);
    if (loadedTemplateUrl !== expectedUrl) {
      debugLog('[HtmlTemplatePreview] waiting correct templateUrl', { expectedUrl, loadedTemplateUrl });
      return;
    }

    // Criar hash dos dados atuais
    const currentHash = createDataHash(formData);
    
    // Pular se nada mudou
    if (lastDataHashRef.current === currentHash) {
      return; // Skip silently - nothing changed
    }
    
    debugLog('[HtmlTemplatePreview] rendering', {
      templateUrl: loadedTemplateUrl || templateUrl,
      templateId: formData?.templateId,
      title: formData?.title,
      hasLogoUrl: !!formData?.logoUrl,
      avatarShape: formData?.avatarShape,
    });

    // Cancelar render pendente
    if (pendingRenderRef.current) {
      cancelAnimationFrame(pendingRenderRef.current);
    }

    // Agendar render no próximo frame
    pendingRenderRef.current = requestAnimationFrame(() => {
      try {
        const templateData = prepareTemplateData(formData, loadedTemplateUrl || templateUrl);

        if (templateData?.logo_url && typeof templateData.logo_url === 'string' && templateData.logo_url.startsWith('/')) {
          console.warn('[HtmlTemplatePreview] logo_url é relativo; pode não carregar no iframe dependendo do host/origem:', {
            logo_url: templateData.logo_url,
            hint: 'prefira URL absoluta (https://upload.../e/...) para garantir render no preview e no site',
          });
        }

        debugLog('[HtmlTemplatePreview] templateData avatar', {
          logo_url: templateData?.logo_url,
          avatar_is_round: templateData?.avatar_is_round,
          avatar_html_kind: templateData?.logo_url ? 'img' : 'initial',
          avatar_html_len: (templateData?.avatar_html || '').length,
        });

        let renderedHtml = renderTemplate(templateHtml, templateData);
        
        // Injetar estilos customizados
        const customStylesForHead = buildCustomStylesForHead(formData.customStyles || {});
        if (customStylesForHead) {
          renderedHtml = renderedHtml.replace('</head>', `<style>${customStylesForHead}</style>\n</head>`);
        }
        
        const sanitizedHtml = sanitizeHtml(renderedHtml);

        // Injetar no iframe
        if (iframeRef.current) {
          const iframeId = Date.now(); // ID único para rastrear este render
          iframeRef.current.srcdoc = sanitizedHtml;
          iframeRef.current.dataset.renderId = iframeId;
          lastDataHashRef.current = currentHash;

          debugLog('[HtmlTemplatePreview] srcdoc set', { iframeId, bytes: sanitizedHtml.length });

          // Resize handler
          const resize = () => {
            try {
              const doc = iframeRef.current?.contentDocument;
              if (doc) {
                const h = Math.max(doc.body?.scrollHeight || 0, doc.documentElement?.scrollHeight || 0, 600);
                iframeRef.current.style.height = `${h}px`;
              }
            } catch {}
          };
          setTimeout(resize, 50);
          setTimeout(resize, 200);
        }
      } catch (err) {
        console.error('[Render] ❌ Error:', err.message);
        setError(err.message);
      }
    });

    return () => {
      if (pendingRenderRef.current) {
        cancelAnimationFrame(pendingRenderRef.current);
      }
    };
  }, [templateHtml, formData, createDataHash, loadedTemplateUrl]);

  // Forçar re-renderização quando template sincroniza
  useEffect(() => {
    const expectedUrl = resolveTemplateUrl(templateUrl);
    if (templateHtml && formData && loadedTemplateUrl === expectedUrl) {
      lastDataHashRef.current = null; // Reset para forçar render
    }
  }, [templateHtml, loadedTemplateUrl, templateUrl, formData]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {loading && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          backgroundColor: '#f5f5f5'
        }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar preview: {error}
        </Alert>
      )}

      <iframe
        ref={iframeRef}
        onLoad={() => {
          debugLog('[HtmlTemplatePreview] iframe onLoad');
        }}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          display: loading || error ? 'none' : 'block',
          backgroundColor: '#fff',
        }}
        title="Pré-visualização do Template"
        sandbox="allow-scripts allow-same-origin"
      />
    </Box>
  );
};

/**
 * Resolver URL relativa do Static para URL completa
 */
function resolveTemplateUrl(url) {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const STATIC_CDN_DEV = 'https://static.dev.agendario.com.br';
  const STATIC_CDN_PROD = 'https://static.agendario.com.br';
  const CDN_BASE = isDevEnvironment() ? STATIC_CDN_DEV : STATIC_CDN_PROD;
  
  return CDN_BASE + url;
}

/**
 * Renderizar template com dados
 * Suporta: {{variable}}, {{#if}}, {{#unless}}, {{#each}}, {{else}}
 */
function renderTemplate(template, data) {
  let html = template;
  html = processEachBlocks(html, data);
  html = processIfBlocks(html, data);
  html = processUnlessBlocks(html, data);
  html = processVariables(html, data);
  return html;
}

/**
 * Processar {{#each}} blocks
 */
function processEachBlocks(html, data) {
  const eachRegex = /\{\{#each\s+(\w+)\}\}([\s\S]*?)\{\{\/each\}\}/g;

  return html.replace(eachRegex, (match, arrayName, blockContent) => {
    const array = getNestedValue(data, arrayName);

    if (!Array.isArray(array) || array.length === 0) {
      return '';
    }

    return array.map((item, index) => {
      const itemData = {
        ...data,
        this: item,
        '@first': index === 0,
        '@last':  index === array.length - 1,
        '@index': index,
      };
      let itemHtml = blockContent;
      // Processar {{#if}} / {{#unless}} aninhados primeiro, depois variáveis
      itemHtml = processIfBlocks(itemHtml, itemData);
      itemHtml = processUnlessBlocks(itemHtml, itemData);
      itemHtml = processVariables(itemHtml, itemData);
      return itemHtml;
    }).join('');
  });
}

/**
 * Processar {{#if}} blocks com suporte a {{else}} e blocos aninhados.
 *
 * Estratégia: combinar apenas os blocos mais internos (sem {{#if}} / {{#unless}}
 * aninhados dentro deles) usando lookahead negativo, repetindo até não haver mais
 * substituições. Isso garante que qualquer profundidade de aninhamento seja
 * resolvida de dentro para fora.
 */
function processIfBlocks(html, data) {
  // Regex que só casa {{#if}} cujo conteúdo NÃO contém outro {{#if}} ou {{#unless}}
  // O grupo de conteúdo usa lookahead negativo: cada char consumido não pode abrir
  // um novo bloco {{#if ou {{#unless
  const INNER_CONTENT = '(?:(?!\\{\\{#(?:if|unless))[\\s\\S])*?';
  const ifElseRegex = new RegExp(
    '\\{\\{#if\\s+([\\w.@]+)\\}\\}(' + INNER_CONTENT + ')\\{\\{else\\}\\}(' + INNER_CONTENT + ')\\{\\{\\/if\\}\\}',
    'g'
  );
  const ifOnlyRegex = new RegExp(
    '\\{\\{#if\\s+([\\w.@]+)\\}\\}(' + INNER_CONTENT + ')\\{\\{\\/if\\}\\}',
    'g'
  );

  let prev;
  do {
    prev = html;

    // 1. if / else
    html = html.replace(ifElseRegex, (match, variableName, ifContent, elseContent) => {
      const value = getNestedValue(data, variableName);
      const isTruthy = value &&
        (typeof value !== 'object' || Object.keys(value).length > 0) &&
        (!Array.isArray(value) || value.length > 0);
      return isTruthy ? ifContent : elseContent;
    });

    // 2. if sem else
    html = html.replace(ifOnlyRegex, (match, variableName, blockContent) => {
      const value = getNestedValue(data, variableName);
      const isTruthy = value &&
        (typeof value !== 'object' || Object.keys(value).length > 0) &&
        (!Array.isArray(value) || value.length > 0);
      return isTruthy ? blockContent : '';
    });
  } while (html !== prev);

  return html;
}

/**
 * Processar {{#unless}} blocks (mesma estratégia innermost-first do processIfBlocks)
 */
function processUnlessBlocks(html, data) {
  const INNER_CONTENT = '(?:(?!\\{\\{#(?:if|unless))[\\s\\S])*?';
  const unlessRegex = new RegExp(
    '\\{\\{#unless\\s+([\\w.@]+)\\}\\}(' + INNER_CONTENT + ')\\{\\{\\/unless\\}\\}',
    'g'
  );

  let prev;
  do {
    prev = html;
    html = html.replace(unlessRegex, (match, variableName, blockContent) => {
      const value = getNestedValue(data, variableName);
      const isFalsy = !value ||
        (typeof value === 'object' && Object.keys(value).length === 0) ||
        (Array.isArray(value) && value.length === 0);
      return isFalsy ? blockContent : '';
    });
  } while (html !== prev);

  return html;
}

/**
 * Processar {{variable}} e {{{variable}}} substitutions
 * Triple mustache {{{var}}} renderiza HTML sem escape
 */
function processVariables(html, data) {
  // Primeiro processar triple mustache (HTML não-escapado)
  const tripleVarRegex = /\{\{\{([\w.]+)\}\}\}/g;
  html = html.replace(tripleVarRegex, (match, varPath) => {
    const value = getNestedValue(data, varPath);
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return '';
    return String(value); // Retorna HTML sem escape
  });

  // Depois processar double mustache (texto escapado)
  const varRegex = /\{\{([\w.]+)\}\}/g;
  return html.replace(varRegex, (match, varPath) => {
    const value = getNestedValue(data, varPath);

    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      return '';
    }

    return String(value);
  });
}

/**
 * Get nested value from object
 */
function getNestedValue(obj, path) {
  if (!path || typeof path !== 'string') {
    return undefined;
  }

  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[part];
  }

  return current;
}

/**
 * Preparar dados para template (mesmo formato que Pages)
 */
function prepareTemplateData(formData, templateUrl) {
  const customStyles = formData.customStyles || {};
  const title = formData.title || 'Untitled';
  const avatarInitial = title.charAt(0).toUpperCase();

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const logoUrl = formData.logoUrl || null;
  const avatarIsRound = formData.avatarShape !== 'free';
  const avatarClass = avatarIsRound ? 'avatar' : 'avatar free-shape';
  const avatarHtml = logoUrl
    ? `<div class="${avatarClass}"><img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(title)}" /></div>`
    : `<div class="avatar">${escapeHtml(avatarInitial)}</div>`;

  // Build social icons com SVG inline (emojis podem não renderizar corretamente)
  const socialIcons = [];
  const socialLinksMap = formData.socialLinks || {};
  const iconMap = {
    instagram: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>',
    twitter: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
    facebook: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
    linkedin: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
    youtube: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>',
    threads: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.858-.712 2.037-1.122 3.412-1.19.978-.048 1.879.024 2.716.204-.084-.784-.296-1.404-.636-1.86-.449-.6-1.148-.923-2.081-.96-1.263-.05-2.143.323-2.69.735l-1.311-1.675c.827-.628 2.163-1.18 3.934-1.109 1.467.058 2.64.578 3.49 1.544.765.87 1.206 2.027 1.312 3.44.459.165.885.375 1.273.628.96.628 1.695 1.478 2.13 2.472.776 1.768.676 4.578-1.528 6.736-1.838 1.8-4.147 2.594-7.263 2.621zm1.597-7.89c-.707-.046-1.98.038-2.937.436-.81.337-1.229.846-1.194 1.45.035.604.477 1.065 1.249 1.298.348.106.733.158 1.136.158.109 0 .22-.004.33-.012 1.16-.063 2.036-.463 2.532-1.157.377-.526.556-1.277.535-2.237-.54-.06-1.1-.066-1.65.064z"/></svg>',
  };
  for (const [platform, linkData] of Object.entries(socialLinksMap)) {
    if (linkData && linkData.url) {
      socialIcons.push({
        url: linkData.url,
        name: linkData.name || platform.charAt(0).toUpperCase() + platform.slice(1),
        icon: iconMap[platform] || '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>',
      });
    }
  }

  // Build links array
  const links = (formData.genericLinks || [])
    .filter(link => link && link.url)
    .map(link => ({
      url: link.url,
      name: link.name || 'Link',
    }));

  // Build custom styles CSS
  const customStylesCSS = buildCustomStylesCSS(customStyles);

  // Converter quebras de linha no subtítulo para <br>
  const subtitleWithBreaks = (formData.subtitle || '').replace(/\n/g, '<br>');

  // Detectar template pelo templateUrl (mais confiável que templateId GUID)
  const urlKey = (templateUrl || '').toLowerCase();

  // Templates profissionais: retornar formData diretamente (estrutura diferente dos link-in-bio)
  const isProfessionalTemplate = urlKey.includes('prof-');
  if (isProfessionalTemplate) {
    // Build booking URL from scheduleId (same pattern as link-in-bio)
    const booking = { ...(formData.booking || {}) };
    if (formData.scheduleId) {
      const shareUrl = import.meta.env.VITE_SHARE_URL || 'https://share.agendario.com.br';
      booking.url = `${shareUrl}/schedule/${formData.scheduleId}`;
    }

    return {
      ...formData,
      booking,
      // Compat keys para retrocompatibilidade com variáveis genéricas
      title: formData.displayName || formData.title || 'Profissional',
      logo_url: formData.photoUrl || null,
      avatar_is_round: (formData.avatarShape || 'round') !== 'free',
      custom_styles: buildCustomStylesCSS(customStyles),
    };
  }

  const isIconsTemplate = urlKey.includes('link-in-bio-icons');
  const isSimpleTemplate = urlKey.includes('link-in-bio-simple');
  const isAgendaFirstTemplate = urlKey.includes('link-in-bio-agenda-first');
  const isSocialAgendaTemplate = urlKey.includes('link-in-bio-social-agenda');
  
  // Agenda - usar agendaLinkName se preenchido, senão scheduleName
  const hasAgenda = !!formData.scheduleId;
  const agendaDisplayName = formData.agendaLinkName?.trim() || formData.scheduleName || 'Agendar';
  const shareUrl = import.meta.env.VITE_SHARE_URL || 'https://share.agendario.com.br';
  const agendaUrl = hasAgenda ? `${shareUrl}/schedule/${formData.scheduleId}` : null;
  const agendaLinkData = hasAgenda
    ? { url: agendaUrl, name: agendaDisplayName }
    : null;

  // Montar estrutura conforme template
  let result = {
    title: formData.title || 'Untitled',
    subtitle: subtitleWithBreaks,
    avatar_initial: avatarInitial,
    logo_url: logoUrl,
    avatar_is_round: avatarIsRound,
    avatar_html: avatarHtml,
    social_icons: socialIcons.length > 0 ? socialIcons : null,
    custom_styles: customStylesCSS,
  };

  // Ícone padrão para links genéricos
  const defaultLinkIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>';
  
  // Ícone de calendário para agenda
  const calendarIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/></svg>';

  if (isAgendaFirstTemplate) {
    // Agenda Primeiro: Agenda → Redes Sociais → Links Genéricos (todos como retângulos)
    const allLinks = [];
    
    // 1. Agenda no topo
    if (agendaLinkData) {
      allLinks.push({ url: agendaLinkData.url, name: agendaLinkData.name, icon: calendarIcon });
    }
    
    // 2. Redes sociais como retângulos
    Object.entries(socialLinksMap)
      .filter(([, linkData]) => linkData && linkData.url)
      .forEach(([platform, linkData]) => {
        allLinks.push({
          url: linkData.url,
          name: linkData.name || platform.charAt(0).toUpperCase() + platform.slice(1),
          icon: iconMap[platform] || null,
        });
      });
    
    // 3. Links genéricos
    (formData.genericLinks || [])
      .filter(link => link && link.url)
      .forEach(link => {
        allLinks.push({ url: link.url, name: link.name || 'Link', icon: defaultLinkIcon });
      });
    
    result.links = allLinks.length > 0 ? allLinks : null;
    result.social_icons = null; // Não usa ícones circulares
  } else if (isSocialAgendaTemplate) {
    // Social + Agenda: Ícones circulares → Agenda (retângulo) → Links genéricos (retângulos)
    result.social_icons = socialIcons.length > 0 ? socialIcons : null;
    
    // Links: Agenda primeiro, depois genéricos
    const allLinks = [];
    if (agendaLinkData) {
      allLinks.push({ url: agendaLinkData.url, name: agendaLinkData.name, icon: calendarIcon });
    }
    (formData.genericLinks || [])
      .filter(link => link && link.url)
      .forEach(link => {
        allLinks.push({ url: link.url, name: link.name || 'Link', icon: defaultLinkIcon });
      });
    
    result.links = allLinks.length > 0 ? allLinks : null;
  } else if (isIconsTemplate) {
    // Com Ícones Sociais: Ícones circulares → Links genéricos → Agenda no final (todos retângulos)
    result.social_icons = socialIcons.length > 0 ? socialIcons : null;
    
    const allLinks = [];
    (formData.genericLinks || [])
      .filter(link => link && link.url)
      .forEach(link => {
        allLinks.push({ url: link.url, name: link.name || 'Link', icon: defaultLinkIcon });
      });
    if (agendaLinkData) {
      allLinks.push({ url: agendaLinkData.url, name: agendaLinkData.name, icon: calendarIcon });
    }
    result.links = allLinks.length > 0 ? allLinks : null;
    // não usar agenda_link aqui para evitar aparecer antes dos links
  } else if (isSimpleTemplate) {
    // Links simples: tudo vira retângulo com ícone à esquerda (inclui redes sociais + agenda)
    const socialAsLinks = Object.entries(socialLinksMap)
      .filter(([, linkData]) => linkData && linkData.url)
      .map(([platform, linkData]) => ({
        url: linkData.url,
        name: linkData.name || platform.charAt(0).toUpperCase() + platform.slice(1),
        icon: iconMap[platform] || null,
      }));
    const genericLinksWithIcons = (formData.genericLinks || [])
      .filter(link => link && link.url)
      .map(link => ({
        url: link.url,
        name: link.name || 'Link',
        icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>',
      }));
    const combinedLinks = [...socialAsLinks, ...genericLinksWithIcons];
    if (agendaLinkData) {
      combinedLinks.push({ 
        url: agendaLinkData.url, 
        name: agendaLinkData.name,
        icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/></svg>',
      });
    }
    result.links = combinedLinks.length > 0 ? combinedLinks : null;
    result.social_icons = null;
    // não usar agenda_link nesse template
  } else {
    // Fallback: manter comportamento padrão (links retangulares, sem agenda, sem ícones)
    result.links = links.length > 0 ? links : null;
    result.social_icons = null;
  }

  return result;
}

/**
 * Build custom styles CSS string para injetar no HEAD
 */
function buildCustomStylesForHead(customStyles) {
  const rules = [];

  // Mapear fontSize para valores em pixels e escala
  const sizeMap = {
    'small': { base: '14px', h1: '24px', subtitle: '14px', link: '13px' },
    'medium': { base: '16px', h1: '28px', subtitle: '16px', link: '15px' },
    'large': { base: '18px', h1: '32px', subtitle: '18px', link: '17px' },
    'extra-large': { base: '20px', h1: '36px', subtitle: '20px', link: '19px' },
  };
  const fontSizes = sizeMap[customStyles.fontSize] || sizeMap['medium'];

  // Aplicar estilos no :root para melhor precedência
  const rootVars = [];
  
  rootVars.push(`--base-font-size: ${fontSizes.base};`);

  // CSS variables para templates profissionais
  if (customStyles.primaryColor) {
    rootVars.push(`--primary: ${customStyles.primaryColor};`);
  }
  if (customStyles.backgroundColor) {
    rootVars.push(`--bg: ${customStyles.backgroundColor};`);
  }
  if (customStyles.textColor) {
    rootVars.push(`--text: ${customStyles.textColor};`);
  }

  if (customStyles.linkColor) {
    rootVars.push(`--link-color: ${customStyles.linkColor};`);
  }

  if (customStyles.linkBackground) {
    rootVars.push(`--link-bg: ${customStyles.linkBackground};`);
  }

  if (rootVars.length > 0) {
    rules.push(`:root { ${rootVars.join(' ')} }`);
  }

  // Aplicar estilos no body
  const bodyStyles = [];

  if (customStyles.backgroundColor) {
    bodyStyles.push(`background-color: ${customStyles.backgroundColor};`);
  }

  if (customStyles.textColor) {
    bodyStyles.push(`color: ${customStyles.textColor};`);
  }

  if (customStyles.fontFamily) {
    const fontFamily = customStyles.fontFamily.includes(' ') 
      ? `"${customStyles.fontFamily}"` 
      : customStyles.fontFamily;
    bodyStyles.push(`font-family: ${fontFamily};`);
  }

  bodyStyles.push(`font-size: ${fontSizes.base};`);

  if (bodyStyles.length > 0) {
    rules.push(`body { ${bodyStyles.join(' ')} }`);
  }

  // Aplicar tamanhos específicos aos elementos do template
  rules.push(`h1 { font-size: ${fontSizes.h1} !important; }`);
  rules.push(`.subtitle { font-size: ${fontSizes.subtitle} !important; }`);
  rules.push(`.link-item { font-size: ${fontSizes.link} !important; }`);

  // Aplicar cores aos links
  if (customStyles.linkColor || customStyles.linkBackground) {
    const linkStyles = [];
    if (customStyles.linkColor) {
      linkStyles.push(`color: ${customStyles.linkColor} !important`);
    }
    if (customStyles.linkBackground) {
      linkStyles.push(`background-color: ${customStyles.linkBackground} !important`);
    }
    rules.push(`.link-item { ${linkStyles.join('; ')}; }`);
  }

  // Aplicar cores aos ícones sociais (template icons)
  if (customStyles.linkColor || customStyles.linkBackground) {
    const socialStyles = [];
    if (customStyles.linkColor) {
      socialStyles.push(`color: ${customStyles.linkColor} !important`);
    }
    if (customStyles.linkBackground) {
      socialStyles.push(`background-color: ${customStyles.linkBackground} !important`);
    }
    rules.push(`.social-icon { ${socialStyles.join('; ')}; }`);
  }

  // Aplicar cor de fundo ao container/card
  if (customStyles.cardBackground) {
    rules.push(`.container { background-color: ${customStyles.cardBackground} !important; }`);
  }

  return rules.join('\n');
}

/**
 * Build custom styles CSS string
 */
function buildCustomStylesCSS(customStyles) {
  const styles = [];

  if (customStyles.backgroundColor) {
    styles.push(`background-color: ${customStyles.backgroundColor};`);
  }

  if (customStyles.textColor) {
    styles.push(`color: ${customStyles.textColor};`);
  }

  if (customStyles.fontFamily) {
    // Adicionar aspas ao font-family se necessário
    const fontFamily = customStyles.fontFamily.includes(' ') 
      ? `"${customStyles.fontFamily}"` 
      : customStyles.fontFamily;
    styles.push(`font-family: ${fontFamily};`);
  }

  if (customStyles.fontSize) {
    const sizeMap = {
      'small': '14px',
      'medium': '16px',
      'large': '18px',
      'extra-large': '20px',
    };
    const fontSize = sizeMap[customStyles.fontSize] || customStyles.fontSize;
    // Usar CSS variable para font-size (template usa --base-font-size)
    styles.push(`--base-font-size: ${fontSize};`);
    styles.push(`font-size: ${fontSize};`);
  }

  if (customStyles.linkColor) {
    styles.push(`--link-color: ${customStyles.linkColor};`);
  }

  if (customStyles.linkBackground) {
    styles.push(`--link-bg: ${customStyles.linkBackground};`);
  }

  return styles.join(' ');
}

/**
 * Sanitizar HTML para remover scripts e event handlers potencialmente perigosos
 * Mantém estrutura HTML mas remove qualquer coisa que possa executar código
 */
function sanitizeHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Remover todas as tags script
  const scripts = doc.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Remover atributos event handler (onclick, onload, etc)
  const allElements = doc.querySelectorAll('*');
  allElements.forEach(el => {
    const eventHandlers = ['onclick', 'onerror', 'onload', 'onmouseover', 'onmouseout', 'onchange', 'oninput'];
    eventHandlers.forEach(handler => {
      if (el.hasAttribute(handler)) {
        el.removeAttribute(handler);
      }
    });
  });
  
  // Preservar DOCTYPE para evitar quirks mode
  const doctype = doc.doctype;
  const doctypeString = doctype 
    ? `<!DOCTYPE ${doctype.name}${doctype.publicId ? ` PUBLIC "${doctype.publicId}"` : ''}${doctype.systemId ? ` "${doctype.systemId}"` : ''}>`
    : '<!DOCTYPE html>';
  
  return doctypeString + '\n' + doc.documentElement.outerHTML;
}

export default HtmlTemplatePreview;
